import { useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { BookOpen, ExternalLink, ArrowLeft, Rows3, FileText } from 'lucide-react';
import { reportApi } from '@/api/report.api';
import { usePermission } from '@/hooks/usePermission';
import { useAuth } from '@/hooks/useAuth';
import { Permissions } from '@/constants/permissions';
import { AppRoutes } from '@/constants/routes';
import { Spinner } from '@/components/ui/Spinner';
import { cn } from '@/utils/cn';
import { ReportToolbar, RunReportButton } from '@/components/reports/ReportToolbar';
import { SavedViewBar } from '@/components/reports/SavedViewBar';
import { AccountSearchInput } from '@/components/reports/AccountSearchInput';
import { LocationSelect } from '@/components/reports/LocationSelect';
import { CostCentreSelect } from '@/components/reports/CostCentreSelect';
import { FinancialPeriodSelect } from '@/components/reports/FinancialPeriodSelect';
import { ReportPrintPreview } from '@/components/reports/ReportPrintPreview';
import { ReportHeader } from '@/components/master/report/ReportHeader';
import { ReportFooter } from '@/components/master/report/ReportFooter';
import { exportToExcel, exportToCsvRows } from '@/utils/reportExport';
import type { ExcelColumn } from '@/utils/reportExport';
import type { AccountLedgerDto, AccountLedgerFilter, AccountLedgerRow } from '@/types/report.types';

/** Presentation-only layout: LEDGER = full columns, STATEMENT = concise statement view. */
type LedgerLayout = 'LEDGER' | 'STATEMENT';

// ── helpers ────────────────────────────────────────────────────────────────────

function today() {
  return new Date().toISOString().split('T')[0];
}
function firstOfMonth() {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().split('T')[0];
}
function fmtAmt(n: number) {
  if (n === 0) return '—';
  return n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
function fmtDate(iso: string | null) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch {
    return iso ?? '—';
  }
}

const INPUT_CLS =
  'w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-1.5 text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-brand-500';
const LABEL_CLS = 'block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1';
const TH_CLS = 'px-3 py-2 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide';
const TH_R_CLS = 'px-3 py-2 text-right text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide';

// ── Running balance display ────────────────────────────────────────────────────

function RunningBalance({ balance, nature }: { balance: number; nature: string }) {
  if (balance === 0) return <span className="text-gray-400 dark:text-gray-500">—</span>;
  const isDebitNature = ['ASSET', 'EXPENSE'].includes(nature.toUpperCase());
  const isNormalSide = isDebitNature ? balance > 0 : balance < 0;
  const abs = Math.abs(balance);
  const label = isDebitNature
    ? balance > 0 ? 'Dr' : 'Cr'
    : balance < 0 ? 'Cr' : 'Dr';
  return (
    <span className={cn(
      'inline-flex items-center gap-1 tabular-nums',
      isNormalSide ? 'text-green-700 dark:text-green-400' : 'text-red-600 dark:text-red-400'
    )}>
      {abs.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
      <span className="text-xs font-semibold">{label}</span>
    </span>
  );
}

function rowBgClass(rowType: AccountLedgerRow['rowType']) {
  if (rowType === 'OPENING') return 'bg-blue-50 dark:bg-blue-900/20 text-blue-800 dark:text-blue-300 font-medium';
  if (rowType === 'CLOSING') return 'bg-blue-50 dark:bg-blue-900/20 text-blue-800 dark:text-blue-300 font-semibold border-t-2 border-blue-200 dark:border-blue-700';
  return '';
}

// ── Excel export ───────────────────────────────────────────────────────────────

const EXCEL_COLUMNS: ExcelColumn[] = [
  { key: 'Date', header: 'Date', width: 14 },
  { key: 'DocNumber', header: 'Doc Number', width: 18 },
  { key: 'Type', header: 'Type', width: 10 },
  { key: 'Reference', header: 'Reference', width: 20 },
  { key: 'Narration', header: 'Narration', width: 36 },
  { key: 'Debit', header: 'Debit', width: 14, numFmt: '#,##0.00' },
  { key: 'Credit', header: 'Credit', width: 14, numFmt: '#,##0.00' },
  { key: 'Balance', header: 'Balance', width: 16, numFmt: '#,##0.00' },
  { key: 'Ccy', header: 'Ccy', width: 8 },
];

function buildExcelRows(data: AccountLedgerDto) {
  return data.rows.map(r => ({
    Date: r.rowType === 'OPENING' ? 'Opening' : r.rowType === 'CLOSING' ? 'Closing' : (r.documentDate ?? ''),
    DocNumber: r.rowType === 'OPENING' ? 'B/F' : r.rowType === 'CLOSING' ? 'C/F' : (r.documentNumber ?? ''),
    Type: r.transactionTypeCode ?? '',
    Reference: r.reference ?? '',
    Narration: r.narration ?? '',
    Debit: r.debit,
    Credit: r.credit,
    Balance: r.runningBalance,
    Ccy: r.currencyCode ?? '',
  }));
}

// ── Stat card ─────────────────────────────────────────────────────────────────

function StatCard({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg px-4 py-3">
      <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">{label}</div>
      <div className="text-sm font-semibold text-gray-900 dark:text-white tabular-nums">{value}</div>
      {sub && <div className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">{sub}</div>}
    </div>
  );
}

// ── Component ──────────────────────────────────────────────────────────────────

export const AccountLedgerPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { hasPermission } = usePermission();
  const { user } = useAuth();

  const urlAccountId = searchParams.get('accountId') ?? '';
  const urlAccountLabel = searchParams.get('accountLabel') ?? '';

  const [filter, setFilter] = useState<AccountLedgerFilter>({
    accountId: urlAccountId,
    fromDate: searchParams.get('fromDate') ?? firstOfMonth(),
    toDate: searchParams.get('toDate') ?? today(),
  });
  const [accountLabel, setAccountLabel] = useState<string>(urlAccountLabel);
  const [layoutMode, setLayoutMode] = useState<LedgerLayout>('LEDGER');
  const [data, setData] = useState<AccountLedgerDto | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPrint, setShowPrint] = useState(false);
  const hasAutoRun = useRef(false);

  const run = useCallback(async (f: AccountLedgerFilter = filter) => {
    if (!f.accountId) {
      setError('Please select an account.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await reportApi.getAccountLedger(f);
      const dto = res.data?.data ?? null;
      setData(dto);
      // Populate account label from loaded data if not already set
      if (dto && !accountLabel) {
        setAccountLabel(`${dto.accountCode} — ${dto.accountName}`);
      }
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load ledger');
    } finally {
      setLoading(false);
    }
  }, [filter, accountLabel]);

  // Auto-run when navigated to with an accountId in URL
  useEffect(() => {
    if (urlAccountId && !hasAutoRun.current) {
      hasAutoRun.current = true;
      run(filter);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!hasPermission(Permissions.RPT_LEDGER)) {
    return (
      <div className="flex items-center justify-center h-full text-gray-500 dark:text-gray-400">
        You do not have permission to view this report.
      </div>
    );
  }

  const nature = data?.accountNature ?? 'ASSET';

  const openingAbs = Math.abs(data?.openingBalance ?? 0);
  const closingAbs = Math.abs(data?.closingBalance ?? 0);
  const openingSide = (data?.openingBalance ?? 0) >= 0 ? 'Dr' : 'Cr';
  const closingSide = (data?.closingBalance ?? 0) >= 0 ? 'Dr' : 'Cr';

  const statement = layoutMode === 'STATEMENT';
  const cellPad = statement ? 'px-3 py-1' : 'px-3 py-2';
  const filterSummary = `${accountLabel || filter.accountId} · ${filter.fromDate}..${filter.toDate} · ${layoutMode}`;

  const handleExcel = () => {
    if (!data) return;
    exportToExcel({
      sheetName: 'Account Ledger',
      columns: EXCEL_COLUMNS,
      rows: buildExcelRows(data),
      title: 'Account Ledger',
      subtitle: `${data.accountCode} — ${data.accountName} · ${filter.fromDate} to ${filter.toDate}`,
    }, `AccountLedger_${data.accountCode}_${filter.fromDate}_${filter.toDate}`);
  };

  const handleCsv = () => {
    if (!data) return;
    exportToCsvRows(
      EXCEL_COLUMNS,
      buildExcelRows(data),
      `AccountLedger_${data.accountCode}_${filter.fromDate}_${filter.toDate}`,
    );
  };

  const applySavedView = (f: unknown) => {
    if (!f || typeof f !== 'object') return;
    const obj = f as Partial<AccountLedgerFilter> & { layoutMode?: LedgerLayout; accountLabel?: string };
    const { layoutMode: savedLayout, accountLabel: savedLabel, ...rest } = obj;
    if (savedLayout === 'LEDGER' || savedLayout === 'STATEMENT') setLayoutMode(savedLayout);
    if (savedLabel) setAccountLabel(savedLabel);
    setFilter(prev => ({ ...prev, ...rest }));
  };

  const handlePrint = () => setShowPrint(true);

  return (
    <div className="flex flex-col h-full gap-4 p-4">
      {/* Page header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            title="Go back"
          >
            <ArrowLeft size={18} />
          </button>
          <BookOpen size={22} className="text-brand-500" />
          <h1 className="text-xl font-bold text-gray-900 dark:text-white">Account Ledger</h1>
          {/* Layout toggle */}
          <div className="flex items-center rounded-lg border border-gray-300 dark:border-gray-600 overflow-hidden ml-2">
            <button
              onClick={() => setLayoutMode('LEDGER')}
              className={cn(
                'flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium transition-colors',
                !statement
                  ? 'bg-brand-600 text-white'
                  : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800'
              )}
              title="Full ledger layout"
            >
              <Rows3 size={13} />
              Ledger
            </button>
            <button
              onClick={() => setLayoutMode('STATEMENT')}
              className={cn(
                'flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium transition-colors',
                statement
                  ? 'bg-brand-600 text-white'
                  : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800'
              )}
              title="Concise statement layout"
            >
              <FileText size={13} />
              Statement
            </button>
          </div>
        </div>
        <ReportToolbar
          onExcelExport={handleExcel}
          onCsvExport={handleCsv}
          onPrint={handlePrint}
          hasData={!!data}
          reportKey="ACCOUNT_LEDGER"
          rowCount={data?.rows.length ?? 0}
          filterSummary={filterSummary}
        />
      </div>

      {/* Saved views */}
      <SavedViewBar
        reportKey="ACCOUNT_LEDGER"
        currentFilters={{ ...filter, layoutMode, accountLabel }}
        onApply={applySavedView}
      />

      {/* Filter Panel */}
      <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-700 p-4 space-y-3">
        {/* Row 1 — Account + dates */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="md:col-span-1">
            <label className={LABEL_CLS}>Account *</label>
            <AccountSearchInput
              accountId={filter.accountId}
              accountLabel={accountLabel}
              onChange={(id, code, name) => {
                setFilter(f => ({ ...f, accountId: id }));
                setAccountLabel(`${code} — ${name}`);
              }}
            />
          </div>
          <div>
            <label className={LABEL_CLS}>From Date *</label>
            <input
              type="date"
              value={filter.fromDate}
              onChange={e => setFilter(f => ({ ...f, fromDate: e.target.value }))}
              className={INPUT_CLS}
            />
          </div>
          <div>
            <label className={LABEL_CLS}>To Date *</label>
            <input
              type="date"
              value={filter.toDate}
              onChange={e => setFilter(f => ({ ...f, toDate: e.target.value }))}
              className={INPUT_CLS}
            />
          </div>
        </div>

        {/* Row 2 — Quick period + location + cost centre */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <label className={LABEL_CLS}>Quick Period</label>
            <FinancialPeriodSelect
              onPeriodChange={(from, to) => {
                setFilter(f => ({ ...f, fromDate: from, toDate: to }));
              }}
            />
          </div>
          <div>
            <label className={LABEL_CLS}>Location</label>
            <LocationSelect
              value={filter.locationId}
              onChange={id => setFilter(f => ({ ...f, locationId: id ?? undefined }))}
            />
          </div>
          <div>
            <label className={LABEL_CLS}>Cost Centre</label>
            <CostCentreSelect
              value={filter.costCenterId}
              onChange={id => setFilter(f => ({ ...f, costCenterId: id ?? undefined }))}
            />
          </div>
        </div>

        <div>
          <RunReportButton
            onClick={() => run(filter)}
            isLoading={loading}
            disabled={!filter.accountId || !filter.fromDate || !filter.toDate}
          />
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 text-sm text-red-700 dark:text-red-400">
          {error}
        </div>
      )}

      {/* Loading (no data yet) */}
      {loading && !data && (
        <div className="flex-1 flex items-center justify-center">
          <Spinner size="lg" />
        </div>
      )}

      {/* Results */}
      {data && (
        <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-700 flex flex-col min-h-0 flex-1">
          {/* Account header */}
          {statement ? (
            <div className="px-4 py-4 border-b border-gray-200 dark:border-gray-700 flex flex-wrap items-end justify-between gap-3">
              <div>
                <div className="text-lg font-bold text-gray-900 dark:text-white">
                  {data.accountCode} — {data.accountName}
                </div>
                <div className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                  Statement · {fmtDate(data.fromDate)} to {fmtDate(data.toDate)} · {data.companyName}
                </div>
              </div>
              <div className="text-right">
                <div className="text-xs text-gray-500 dark:text-gray-400">Opening Balance</div>
                <div className="text-lg font-bold text-gray-900 dark:text-white tabular-nums">
                  {fmtAmt(openingAbs)} {openingSide}
                </div>
              </div>
            </div>
          ) : (
            <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
              <div className="text-sm font-semibold text-gray-900 dark:text-white">
                {data.accountCode} — {data.accountName}
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                {data.accountType} · {nature} · {data.companyName} · {fmtDate(data.fromDate)} to {fmtDate(data.toDate)}
              </div>
            </div>
          )}

          {/* Summary bar */}
          {!statement && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 px-4 py-3 border-b border-gray-200 dark:border-gray-700">
              <StatCard
                label="Opening Balance"
                value={`${fmtAmt(openingAbs)} ${openingSide}`}
              />
              <StatCard label="Total Debit" value={fmtAmt(data.totalDebit)} />
              <StatCard label="Total Credit" value={fmtAmt(data.totalCredit)} />
              <StatCard
                label="Closing Balance"
                value={`${fmtAmt(closingAbs)} ${closingSide}`}
              />
            </div>
          )}

          {/* Print area */}
          <div data-print-section id="report-print-area" className="overflow-auto flex-1">
            {/* Print-only header */}
            <div className="hidden print:block px-4 pt-4 pb-2 text-center">
              <div className="text-lg font-bold">{data.companyName}</div>
              <div className="text-base font-semibold mt-1">Account Ledger</div>
              <div className="text-sm text-gray-600 dark:text-gray-400 mt-0.5">
                {data.accountCode} — {data.accountName}
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400">{fmtDate(data.fromDate)} to {fmtDate(data.toDate)}</div>
            </div>

            <table className={cn('w-full text-sm border-collapse', statement ? 'min-w-[820px]' : 'min-w-[1000px]')}>
              <thead className="sticky top-0 bg-gray-50 dark:bg-gray-800 z-10">
                <tr>
                  <th className={cn(TH_CLS, 'w-28')}>Date</th>
                  <th className={cn(TH_CLS, 'w-32')}>Doc Number</th>
                  <th className={cn(TH_CLS, 'w-20')}>Type</th>
                  <th className={cn(TH_CLS, 'w-28')}>Reference</th>
                  <th className={TH_CLS}>Narration</th>
                  <th className={cn(TH_R_CLS, 'w-28')}>Debit</th>
                  <th className={cn(TH_R_CLS, 'w-28')}>Credit</th>
                  <th className={cn(TH_R_CLS, 'w-32')}>Balance</th>
                  {!statement && <th className={cn(TH_CLS, 'w-12')}>Ccy</th>}
                </tr>
              </thead>
              <tbody>
                {data.rows.map((row, idx) => (
                  <tr
                    key={`${row.journalId ?? 'special'}-${idx}`}
                    className={cn(
                      'border-t border-gray-100 dark:border-gray-800',
                      row.rowType === 'TRANSACTION' && 'hover:bg-gray-50 dark:hover:bg-gray-800/50',
                      rowBgClass(row.rowType)
                    )}
                  >
                    {/* Date */}
                    <td className={cn(cellPad, 'text-sm')}>
                      {row.rowType === 'OPENING'
                        ? 'Opening'
                        : row.rowType === 'CLOSING'
                          ? 'Closing'
                          : fmtDate(row.documentDate)}
                    </td>

                    {/* Doc Number */}
                    <td className={cn(cellPad, 'text-sm')}>
                      {row.journalId && row.documentNumber ? (
                        <button
                          onClick={() =>
                            navigate(AppRoutes.GL_JOURNAL_DETAIL.replace(':id', row.journalId!))
                          }
                          className="text-brand-600 dark:text-brand-400 hover:underline flex items-center gap-1"
                        >
                          {row.documentNumber}
                          <ExternalLink size={11} />
                        </button>
                      ) : (
                        <span className="text-gray-400 dark:text-gray-500 italic">
                          {row.rowType === 'OPENING' ? 'B/F' : row.rowType === 'CLOSING' ? 'C/F' : '—'}
                        </span>
                      )}
                    </td>

                    {/* Type */}
                    <td className={cn(cellPad, 'text-xs text-gray-500 dark:text-gray-400')}>
                      {row.transactionTypeCode ?? '—'}
                    </td>

                    {/* Reference */}
                    <td className={cn(cellPad, 'text-sm text-gray-600 dark:text-gray-400')}>
                      {row.reference ?? '—'}
                    </td>

                    {/* Narration */}
                    <td className={cn(cellPad, 'text-sm text-gray-700 dark:text-gray-300 max-w-xs truncate')} title={row.narration ?? ''}>
                      {row.narration ?? '—'}
                    </td>

                    {/* Debit */}
                    <td className={cn(cellPad, 'text-sm text-right tabular-nums text-gray-900 dark:text-gray-100')}>
                      <div>{row.debit !== 0 ? fmtAmt(row.debit) : '—'}</div>
                      {!statement && row.foreignDebit > 0 && (
                        <div className="text-xs text-gray-400 dark:text-gray-500">
                          {row.currencyCode} {row.foreignDebit.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </div>
                      )}
                    </td>

                    {/* Credit */}
                    <td className={cn(cellPad, 'text-sm text-right tabular-nums text-gray-900 dark:text-gray-100')}>
                      <div>{row.credit !== 0 ? fmtAmt(row.credit) : '—'}</div>
                      {!statement && row.foreignCredit > 0 && (
                        <div className="text-xs text-gray-400 dark:text-gray-500">
                          {row.currencyCode} {row.foreignCredit.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </div>
                      )}
                    </td>

                    {/* Running balance */}
                    <td className={cn(cellPad, 'text-sm text-right')}>
                      <RunningBalance balance={row.runningBalance} nature={nature} />
                    </td>

                    {/* Currency */}
                    {!statement && (
                      <td className={cn(cellPad, 'text-xs text-gray-400 dark:text-gray-500')}>
                        {row.currencyCode ?? '—'}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {!data && !loading && (
        <div className="flex-1 flex items-center justify-center text-gray-400 dark:text-gray-600 text-sm">
          {filter.accountId
            ? 'Click "Run Report" to load the account ledger.'
            : 'Select an account and date range, then click "Run Report".'}
        </div>
      )}

      {/* ── Print Preview ─────────────────────────────────────────────────── */}
      {data && (
        <ReportPrintPreview
          isOpen={showPrint}
          onClose={() => setShowPrint(false)}
          title="Account Ledger"
          orientation="landscape"
        >
          <ReportHeader
            reportTitle="Account Ledger"
            printedBy={user?.fullName}
            printedAt={new Date()}
            meta={[
              { label: 'Company', value: data.companyName },
              { label: 'Account', value: `${data.accountCode} — ${data.accountName}` },
              { label: 'Period',  value: `${fmtDate(data.fromDate)} to ${fmtDate(data.toDate)}` },
              { label: 'Type',    value: `${data.accountType} (${data.accountNature ?? ''})` },
            ]}
          />
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '8.5px', marginTop: '8px' }}>
            <thead>
              <tr>
                {['Date','Doc Number','Type','Reference','Narration','Debit','Credit','Balance','Ccy'].map((h, i) => (
                  <th key={h} style={{ background: '#1e3a5f', color: 'white', padding: '5px 6px', textAlign: i >= 5 && i <= 7 ? 'right' : 'left', fontWeight: 700, whiteSpace: 'nowrap', fontSize: '8.5px' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.rows.map((row: AccountLedgerRow, idx: number) => {
                const isSpecial = row.rowType !== 'TRANSACTION';
                return (
                  <tr key={idx} style={{ borderBottom: '1px solid #f3f4f6', background: isSpecial ? '#eff6ff' : undefined }}>
                    <td style={{ padding: '3px 6px', whiteSpace: 'nowrap' }}>
                      {row.rowType === 'OPENING' ? 'Opening' : row.rowType === 'CLOSING' ? 'Closing' : fmtDate(row.documentDate ?? null)}
                    </td>
                    <td style={{ padding: '3px 6px', fontFamily: 'monospace' }}>
                      {row.rowType === 'OPENING' ? 'B/F' : row.rowType === 'CLOSING' ? 'C/F' : (row.documentNumber ?? '—')}
                    </td>
                    <td style={{ padding: '3px 6px', color: '#6b7280', fontSize: '8px' }}>{row.transactionTypeCode ?? '—'}</td>
                    <td style={{ padding: '3px 6px', color: '#6b7280' }}>{row.reference ?? '—'}</td>
                    <td style={{ padding: '3px 6px', maxWidth: '160px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.narration ?? '—'}</td>
                    <td style={{ padding: '3px 6px', textAlign: 'right', fontFamily: 'monospace' }}>{row.debit !== 0 ? fmtAmt(row.debit) : '—'}</td>
                    <td style={{ padding: '3px 6px', textAlign: 'right', fontFamily: 'monospace' }}>{row.credit !== 0 ? fmtAmt(row.credit) : '—'}</td>
                    <td style={{ padding: '3px 6px', textAlign: 'right', fontFamily: 'monospace', fontWeight: isSpecial ? 700 : 400 }}>{fmtAmt(Math.abs(row.runningBalance))}{row.runningBalance < 0 ? ' Cr' : row.runningBalance > 0 ? ' Dr' : ''}</td>
                    <td style={{ padding: '3px 6px', color: '#6b7280', fontSize: '8px' }}>{row.currencyCode ?? '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <ReportFooter />
        </ReportPrintPreview>
      )}
    </div>
  );
};
